import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Alert, Button } from "north.js";
import React from "react";
import styled from "styled-components";

import { Link } from "src/shared/components";
import { errorToast, successToast } from "src/shared/components/Toast";
import { Route } from "src/shared/constants/route";
import { useEventContext } from "src/shared/contexts";
import { useUserContext } from "src/shared/contexts/UserContext";
import { SlackIcon } from "src/static/icons";
import { useCreateDirectorySlackConversationMutation } from "src/views/hacker-directory/graphql/createDirectorySlackConversation.generated";

import { getDirectoryConversationErrorMessage } from "./errorMessage";

const SlackConnectButton: React.FC<{ claimId: number }> = ({ claimId }) => {
  const { info } = useUserContext();
  const { hackerDirectory } = useEventContext();
  const [createConversation, { loading }] =
    useCreateDirectorySlackConversationMutation();
  const slackLinkingDisabled = !hackerDirectory.slackLinkingEnabled;
  const tooltipId = `slack-linking-tooltip-${claimId}`;

  const button = (
    <Button
      color="secondary"
      size="lg"
      disabled={
        loading || slackLinkingDisabled || !info?.event_platform_user_id
      }
      style={{ alignSelf: "flex-start" }}
      trailingIcon={
        loading ? undefined : (
          <SlackIcon aria-hidden style={{ width: 22, height: 22 }} />
        )
      }
      onClick={async () => {
        try {
          await createConversation({
            variables: { targetClaimId: claimId },
          });
          successToast(
            "Slack conversation created! Check Slack to start chatting."
          );
        } catch (error) {
          errorToast(getDirectoryConversationErrorMessage(error));
        }
      }}
    >
      {loading ? "Starting conversation..." : "Start a conversation on Slack"}
    </Button>
  );

  if (slackLinkingDisabled) {
    return (
      <TooltipTarget tabIndex={0} aria-describedby={tooltipId}>
        {button}
        <SlackLinkingTooltip id={tooltipId} role="tooltip">
          slack linking coming soon!
        </SlackLinkingTooltip>
      </TooltipTarget>
    );
  }

  return info?.event_platform_user_id ? (
    button
  ) : (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      <Alert
        color="warning"
        icon={<ExclamationTriangleIcon width={20} height={20} />}
      >
        Link your Slack account from{" "}
        <Link href={Route.HACKER_DIRECTORY_YOUR_PROFILE}>My Profile</Link>{" "}
        before starting a conversation.
      </Alert>
      {button}
    </div>
  );
};

export default SlackConnectButton;

const TooltipTarget = styled.span`
  position: relative;
  display: inline-flex;
  align-self: flex-start;

  &:hover [role="tooltip"],
  &:focus [role="tooltip"] {
    visibility: visible;
    opacity: 1;
  }
`;

const SlackLinkingTooltip = styled.span`
  position: absolute;
  z-index: 2;
  top: calc(100% + 8px);
  left: 50%;
  width: max-content;
  max-width: 200px;
  padding: 8px 12px;
  transform: translateX(-50%);
  visibility: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, visibility 120ms ease;
`;
